import { useState, useCallback, useRef, useEffect } from "react";
import { sendOtp, verifyOtp, getNextUid } from "../utils/api";

const SUBMIT_KEY = "contactPopupSubmittedAt";
const TTL_MS = 24 * 60 * 60 * 1000;

function getDomainWithMainPath() {
  const domain = window.location.hostname.replace("www.", "");
  const segment = window.location.pathname.split("/").filter(Boolean)[0];
  return segment ? `${domain}/${segment}` : domain;
}

export function useLeadForm({ projectId, formDefaults = {}, floorOfferText = "" }) {
  const [modal, setModal] = useState("closed");
  const [c2aEvent, setC2aEvent] = useState("PopUp");
  // ✅ NEW: optional site-visit / interaction tagging, set only when the
  // popup was opened from the Site Visit section's CTA (see
  // SiteVisitSection.jsx + App.jsx). Every other CTA leaves this null, so
  // handleSendOtp's payload below is unchanged for them.
  const [interaction, setInteraction] = useState(null);

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
  });

  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);

  const uidRef = useRef(null);
  const autoPopupRef = useRef(null);
  const countdownRef = useRef(null);

  // 🔥 NEW: popup counter
  const popupCountRef = useRef(0);

  // ============================
  // ✅ SAFE ACCESSORS
  // ============================
  const getProjectName = useCallback(
    () => formDefaults?.project || "",
    [formDefaults]
  );

  const getLocation = useCallback(
    () => formDefaults?.location || "",
    [formDefaults]
  );

  // ============================
  // ✅ CHECK 24h SUBMISSION
  // ============================
  const isAlreadySubmitted = useCallback(() => {
    try {
      const ts = localStorage.getItem(SUBMIT_KEY);
      if (!ts) return false;
      return Date.now() - Number(ts) < TTL_MS;
    } catch {
      return false;
    }
  }, []);

  // ============================
  // 🔥 POPUP DELAY LOGIC
  // ============================
  const getNextPopupDelay = () => {
    popupCountRef.current++;

    if (popupCountRef.current === 1) return 3000;
    if (popupCountRef.current === 2) return 5000;
    return 10000;
  };

  const scheduleAutoPopup = useCallback(() => {
    clearTimeout(autoPopupRef.current);

    const delay = getNextPopupDelay();

    autoPopupRef.current = setTimeout(() => {
      setModal((prev) => (prev === "closed" ? "contact" : prev));
    }, delay);
  }, []);

  // ============================
  // ✅ OPEN POPUP (manual)
  // ============================
  const openPopup = useCallback(
    (event = "PopUp", interactionMeta = null) => {
      setC2aEvent(event);
      setInteraction(interactionMeta);

      if (isAlreadySubmitted()) {
        setModal("already");
      } else {
        setModal("contact");
      }
    },
    [isAlreadySubmitted]
  );

  // ============================
  // ✅ CLOSE POPUP + RESCHEDULE
  // ============================
  const closeAll = useCallback(() => {
    setModal("closed");

    if (!isAlreadySubmitted()) {
      scheduleAutoPopup(); // 🔥 schedule next popup
    }
  }, [isAlreadySubmitted, scheduleAutoPopup]);

  // ============================
  // 🔥 INITIAL AUTO POPUP
  // ============================
  useEffect(() => {
    if (!formDefaults?.project || !formDefaults?.location) return;
    if (isAlreadySubmitted()) return;

    scheduleAutoPopup();

    return () => clearTimeout(autoPopupRef.current);
  }, [formDefaults, isAlreadySubmitted, scheduleAutoPopup]);

  // ============================
  // ⏳ RESEND COUNTDOWN
  // ============================
  const startResendCountdown = () => {
    clearInterval(countdownRef.current);
    setResendCountdown(60);

    countdownRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ============================
  // 📩 SEND OTP
  // ============================
  const handleSendOtp = useCallback(
    async ({ fullName, phone, email, countryCode, countryIso, interaction: interactionArg }) => {
      setLoading(true);
      setOtpError("");

      try {
        if (!uidRef.current) {
          uidRef.current = await getNextUid();
        }

        const uid = uidRef.current;
        // ✅ Default to India ("91") when a country wasn't supplied, so
        //    older callers of handleSendOtp keep working unchanged.
        const resolvedCountryCode = countryCode || "91";
        const resolvedCountryIso = countryIso || "IN";

        setFormData({
          fullName,
          phone,
          email: email || "",
          uniqueId: uid,
          countryCode: resolvedCountryCode,
          countryIso: resolvedCountryIso,
        });

        const payload = {
          phone,
          // ✅ Backend/SMS gateway needs the dial code to route the OTP to
          //    the right country — sent both as its own field and as a
          //    combined E.164-ish string for backends expecting one field.
          countryCode: resolvedCountryCode,
          countryIso: resolvedCountryIso,
          phoneWithCode: `+${resolvedCountryCode}${phone}`,
          fullName,
          // ✅ Only the Site Visit form collects this today; other CTAs send
          // "" and the backend already defaults missing email to "".
          email: email || "",
          projectName: getProjectName(),
          location: getLocation(),
          domainName: getDomainWithMainPath(),
          uniqueId: uid,
          c2aEvent: c2aEvent || "Form Submit",
          // ✅ NEW: only present for the Site Visit CTA — see openPopup above.
          // `interactionArg` (passed straight in by startSiteVisitFlow) wins
          // over the `interaction` state var, since a setInteraction() call
          // made just before calling handleSendOtp in the same event handler
          // hasn't taken effect in this closure yet (React state updates
          // land on the next render) — passing it explicitly avoids sending
          // a stale/null interaction on that first call.
          interaction: interactionArg !== undefined ? interactionArg : (interaction || undefined),
        };

        console.log("📤 SEND OTP:", payload);

        const result = await sendOtp(payload);
        if (result?.error) throw new Error(result.error);

        setModal("otp");
        startResendCountdown();
        return true; // ✅ lets callers (e.g. startSiteVisitFlow) know the OTP actually went out
      } catch (e) {
        setOtpError(e.message || "Failed to send OTP");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [getProjectName, getLocation, c2aEvent, interaction]
  );

  // ============================
  // 🏠 SITE VISIT FLOW (dedicated form -> shared OTP screen)
  // ============================
  // Called by SiteVisitSection's inline Name/Phone/Email/Date form once
  // submitted. Tags this lead with the Site Visit interaction fields and
  // sends the OTP straight away — skipping LeadPopup's own "contact" step
  // entirely, so the visitor lands directly on the same OTP screen every
  // other CTA uses (see components/LeadPopup.jsx, unchanged). The CRM push
  // itself (with interaction_* fields) happens backend-side in
  // routes/leads.js's /verify-otp handler once the OTP is confirmed.
  const startSiteVisitFlow = useCallback(
    async ({ fullName, phone, email, interaction: interactionMeta, event = "Book Site Visit" }) => {
      setC2aEvent(event);
      setInteraction(interactionMeta);

      if (isAlreadySubmitted()) {
        setModal("already");
        return true; // ✅ handled — LeadPopup shows the "already submitted" state
      }

      // ✅ Returns true/false so callers can tell whether the OTP actually
      // went out — on failure this stays false and otpError is set, which
      // SiteVisitSection surfaces inline via its `error` prop.
      return handleSendOtp({
        fullName,
        phone,
        email,
        countryCode: "91",
        countryIso: "IN",
        interaction: interactionMeta,
      });
    },
    [isAlreadySubmitted, handleSendOtp]
  );

  // ============================
  // 🔐 VERIFY OTP
  // ============================
  const handleVerifyOtp = useCallback(
    async (otp) => {
      setLoading(true);
      setOtpError("");

      try {
        const { fullName, phone, email, uniqueId, countryCode, countryIso } = formData;
        const resolvedCountryCode = countryCode || "91";

        const payload = {
          phone,
          otp,
          countryCode: resolvedCountryCode,
          countryIso: countryIso || "IN",
          phoneWithCode: `+${resolvedCountryCode}${phone}`,
          fullName,
          email,
          projectName: getProjectName(),
          location: getLocation(),
          domainName: getDomainWithMainPath(),
          uniqueId,
          c2aEvent: c2aEvent || "Form Submit",
        };

        console.log("📥 VERIFY OTP:", payload);

        const result = await verifyOtp(payload);

        if (result?.error) {
          setOtpError(result.error);
          return;
        }

        localStorage.setItem(SUBMIT_KEY, Date.now().toString());
        uidRef.current = null;

        setModal("thankyou");

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "thankYouPopupVisible",
          lead_name: fullName,
          lead_phone: phone,
          project_name: getProjectName(),
          lead_source: c2aEvent || "Form Submit",
        });

        setTimeout(() => {
          setModal("closed");
        }, 4000);
      } catch (e) {
        setOtpError(e.message || "Verification failed");
      } finally {
        setLoading(false);
      }
    },
    [formData, getProjectName, getLocation, c2aEvent]
  );

  // ============================
  // 🔁 RESEND OTP
  // ============================
  const handleResendOtp = useCallback(async () => {
    if (resendCountdown > 0) return;
    await handleSendOtp(formData);
  }, [handleSendOtp, formData, resendCountdown]);

  // ============================
  // ↩️ BACK TO CONTACT (edit number)
  // ============================
  // ✅ FIX: previously the OTP screen's "Back" button called
  //    window.location.reload(), which wiped everything and reloaded the
  //    whole page just to fix a typo'd phone number. This instead returns
  //    to the contact step in-place — formData (name/phone already typed)
  //    is preserved so ContactForm can prefill it.
  const backToContact = useCallback(() => {
    clearInterval(countdownRef.current);
    setResendCountdown(0);
    setOtpError("");
    setModal("contact");
  }, []);

  // ============================
  // 🧹 CLEANUP
  // ============================
  useEffect(() => {
    return () => {
      clearTimeout(autoPopupRef.current);
      clearInterval(countdownRef.current);
    };
  }, []);

  return {
    modal,
    loading,
    otpError,
    resendCountdown,
    formData,
    c2aEvent,
    formDefaults,
    floorOfferText,

    openPopup,
    closeAll,
    handleSendOtp,
    startSiteVisitFlow,
    handleVerifyOtp,
    handleResendOtp,
    backToContact,
    setFormData,
  };
}